import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from "@cleanbudget/contract/list";
import { StockFilter, StockMovementKind } from "@cleanbudget/contract/stock";
import { z } from "zod";

/**
 * The stock screens' URLs. The API's queries (`StockListQuery`, `StockMovementQuery`) refuse a bad
 * value with 400; a URL is typed by hand too, so these forgive one (`?page=abc` is page 1) and hand
 * the API only what it takes.
 */

const page = z.coerce.number().int().min(1).max(100_000).default(1).catch(1);
const pageSize = z.coerce
	.number()
	.pipe(z.literal(PAGE_SIZES))
	.default(DEFAULT_PAGE_SIZE)
	.catch(DEFAULT_PAGE_SIZE);
const search = z.string().trim().max(200).optional().catch(undefined);

/** `/stock`: search text, the low / out filter, the page. */
export const StockSearch = z.object({
	search,
	stock: StockFilter.optional().catch(undefined),
	/** Only what the shop sells: active variants of active products (the start page links here). */
	activeOnly: z.enum(["yes"]).optional().catch(undefined),
	page,
	pageSize,
});
export type StockSearch = z.infer<typeof StockSearch>;

/** `/stock/$variantId`: the history's kind filter and page. */
export const HistorySearch = z.object({
	kind: StockMovementKind.optional().catch(undefined),
	page,
	pageSize,
});
export type HistorySearch = z.infer<typeof HistorySearch>;

/** `/stock/quick`: what was typed or scanned, and the variant picked from the results. */
export const QuickSearch = z.object({
	search,
	variantId: z.string().min(1).max(100).optional().catch(undefined),
});
export type QuickSearch = z.infer<typeof QuickSearch>;

/** The defaults, left out of the URL. */
export const LIST_DEFAULTS = { page: 1, pageSize: DEFAULT_PAGE_SIZE } as const;
